import { scaleLinear } from 'd3-scale';
import { area, curveMonotoneX, line } from 'd3-shape';
import { useMemo } from 'react';
import type { Day } from '@map-my-money/engine';
import { t } from '../../i18n/index.ts';
import { axisMoney, compactMoney, money, monthYear, yearOnly } from '../../lib/format.ts';
import { useWidth } from '../../lib/hooks.ts';
import { clamp, moneyTicks, relativeYearTicks, textWidth, yearTicks } from './chartUtils.ts';
import { ScrubOverlay, useScrub } from './useScrub.ts';

/** The impact pill sits just left of the end points: above both lines when there is room, else below. */
const pillY = (modifiedY: number, baselineY: number, height: number) => {
  const above = Math.min(modifiedY, baselineY) - 34;
  return above >= 0 ? above : clamp(Math.max(modifiedY, baselineY) + 12, 0, height - 26);
};

interface Point {
  readonly date: Day;
  readonly value: number;
}

/** Current plan (dashed slate) against the plan with changes (cyber), the gap between them shaded. */
export function CompareChart({
  baseline, modified, height, compact = false, label, accent = 'cyber', hideAmounts = false, yearAxis = 'calendar', zoom = false, diffHeight = 0,
}: {
  baseline: readonly Point[];
  modified: readonly Point[];
  height: number;
  compact?: boolean;
  label: string;
  accent?: 'cyber' | 'mint';
  hideAmounts?: boolean;
  yearAxis?: 'calendar' | 'relative';
  /** Fit the value axis to the data instead of starting at £0, so a small gap between the lines is visible. */
  zoom?: boolean;
  /** Height of a strip under the chart plotting the difference (with changes − current) over time. */
  diffHeight?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const margin = {
    top: compact ? 12 : 16,
    right: compact ? 10 : 16,
    bottom: compact ? 22 : 28,
    left: hideAmounts ? 10 : compact ? 48 : 58,
  };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);
  const stripGap = diffHeight > 0 ? (compact ? 18 : 22) : 0;
  const plotH = Math.max(0, innerH - diffHeight - stripGap);
  const stroke = accent === 'mint' ? 'var(--mint)' : 'var(--cyber)';

  const layout = useMemo(() => {
    if (baseline.length < 2 || modified.length < 2 || innerW <= 0) return null;
    const all = [...baseline, ...modified].map((p) => p.value);
    const minV = Math.min(...all);
    const maxV = Math.max(...all);
    const floor = zoom ? Math.max(Math.min(0, minV), minV - (maxV - minV) * 0.12) : Math.min(0, minV);
    const { ticks, lo, hi } = moneyTicks(floor, maxV, compact ? 3 : 4, zoom && floor > 0);
    const first = baseline[0]!.date;
    const last = baseline[baseline.length - 1]!.date;
    const x = scaleLinear().domain([first, last]).range([0, innerW]);
    const y = scaleLinear().domain([lo, hi]).range([plotH, 0]);
    const diffs = baseline.map((b, i) => ({ date: b.date, value: (modified[i]?.value ?? b.value) - b.value }));
    const dLo = Math.min(0, ...diffs.map((d) => d.value));
    const dHi = Math.max(0, ...diffs.map((d) => d.value));
    const stripTop = plotH + stripGap;
    const yd = scaleLinear().domain([dLo, dHi === dLo ? dLo + 1 : dHi]).range([stripTop + diffHeight, stripTop + 4]);
    const diffArea = area<Point>().x((p) => x(p.date)).y0(() => yd(0)).y1((p) => yd(p.value)).curve(curveMonotoneX)(diffs) ?? '';
    const diffLine = line<Point>().x((p) => x(p.date)).y((p) => yd(p.value)).curve(curveMonotoneX)(diffs) ?? '';
    const pathOf = (pts: readonly Point[]) => line<Point>().x((p) => x(p.date)).y((p) => y(p.value)).curve(curveMonotoneX)(pts) ?? '';
    const gap = area<[Point, Point]>()
      .x(([b]) => x(b.date))
      .y0(([b]) => y(b.value))
      .y1(([, m]) => y(m.value))
      .curve(curveMonotoneX)(baseline.map((b, i) => [b, modified[i] ?? b] as [Point, Point])) ?? '';
    const maxTicks = Math.max(3, Math.floor(innerW / (compact ? 56 : 84)));
    const xTicks = yearAxis === 'relative' ? relativeYearTicks(first, last, maxTicks) : yearTicks(first, last, maxTicks);
    const xs = baseline.map((p) => x(p.date));
    const endB = baseline[baseline.length - 1]!;
    const endM = modified[modified.length - 1]!;
    return { x, y, ticks, gap, base: pathOf(baseline), mod: pathOf(modified), xTicks, xs, endB, endM, first, yd, diffArea, diffLine, stripTop, diffs };
  }, [baseline, modified, innerW, plotH, compact, yearAxis, zoom, diffHeight, stripGap]);

  const { index, handlers } = useScrub(layout?.xs ?? []);
  const activeB = index !== null ? baseline[index] : undefined;
  const activeM = index !== null ? modified[index] : undefined;
  const impact = layout ? layout.endM.value - layout.endB.value : 0;
  const pillText = compactMoney(impact, { sign: true });
  const pillW = textWidth(pillText, 13, 800) + 22;

  const tickLabel = (tick: Day) => {
    if (yearAxis === 'calendar' || !layout) return yearOnly(tick);
    return t('chart.yearN', { n: Math.round((tick - layout.first) / 365.25) });
  };

  return (
    <figure className="chart">
      <div ref={ref} className="chart__frame" style={{ height }}>
        {layout && (
          <svg width={width} height={height} aria-hidden="true">
            <g transform={`translate(${margin.left},${margin.top})`}>
              {layout.ticks.map((tick) => (
                <g key={tick}>
                  <line x1={0} x2={innerW} y1={layout.y(tick)} y2={layout.y(tick)} className={tick === 0 || tick === layout.ticks[0] ? 'chart__gridline chart__gridline--base' : 'chart__gridline'} />
                  {!hideAmounts && (
                    <text x={-10} y={layout.y(tick)} dy="0.34em" textAnchor="end" className="chart__axis-label">{axisMoney(tick)}</text>
                  )}
                </g>
              ))}
              {layout.xTicks.map((tick, i) => (
                <text key={tick} x={layout.x(tick)} y={innerH + (compact ? 16 : 20)} textAnchor={i === 0 && layout.x(tick) < 20 ? 'start' : i === layout.xTicks.length - 1 && layout.x(tick) > innerW - 20 ? 'end' : 'middle'} className="chart__axis-label">
                  {tickLabel(tick)}
                </text>
              ))}
              <g className="chart__reveal">
                <path d={layout.gap} fill={impact >= 0 ? 'var(--mint)' : 'var(--amber)'} fillOpacity={0.22} />
                <path d={layout.base} fill="none" stroke="var(--slate)" strokeWidth={2} strokeDasharray="6 5" />
                <path d={layout.mod} fill="none" stroke={stroke} strokeWidth={compact ? 2.5 : 3} strokeLinecap="round" />
              </g>
              <circle cx={innerW} cy={layout.y(layout.endB.value)} r={5} fill="var(--slate)" stroke="var(--card)" strokeWidth={3} />
              <circle cx={innerW} cy={layout.y(layout.endM.value)} r={6} fill={stroke} stroke="var(--card)" strokeWidth={3} />
              {diffHeight > 0 && (
                <g className="diff-strip">
                  <line x1={0} x2={innerW} y1={layout.yd(0)} y2={layout.yd(0)} className="chart__gridline chart__gridline--base" />
                  <path d={layout.diffArea} fill={impact >= 0 ? 'var(--mint)' : 'var(--amber)'} fillOpacity={0.2} className="chart__reveal" />
                  <path d={layout.diffLine} fill="none" stroke={impact >= 0 ? 'var(--mint)' : 'var(--amber)'} strokeWidth={1.5} className="chart__reveal" />
                  <text x={0} y={layout.stripTop - 6} className="diff-strip__label">{t('whatIf.difference')}</text>
                  {!hideAmounts && (
                    <text x={innerW} y={layout.stripTop - 6} textAnchor="end" className={`diff-strip__value ${impact >= 0 ? 'text-mint-fill' : 'text-amber-fill'}`}>
                      {compactMoney(impact, { sign: true })}
                    </text>
                  )}
                </g>
              )}
              {!hideAmounts && Math.abs(impact) >= 1 && diffHeight === 0 && (
                <g transform={`translate(${innerW - pillW - 14},${pillY(layout.y(layout.endM.value), layout.y(layout.endB.value), plotH)})`}>
                  <rect width={pillW} height={24} rx={8} className={impact >= 0 ? 'impact-pill' : 'impact-pill impact-pill--down'} />
                  <text x={pillW / 2} y={16.5} textAnchor="middle" className={impact >= 0 ? 'impact-pill__text' : 'impact-pill__text impact-pill__text--down'}>{pillText}</text>
                </g>
              )}
              {index !== null && activeB && activeM && (
                <g pointerEvents="none" className="chart__crosshair">
                  <line x1={layout.xs[index]} x2={layout.xs[index]} y1={0} y2={innerH} />
                  {diffHeight > 0 && layout.diffs[index] && <circle cx={layout.xs[index]} cy={layout.yd(layout.diffs[index]!.value)} r={3.5} fill="#fff" />}
                  <circle cx={layout.xs[index]} cy={layout.y(activeB.value)} r={4} fill="var(--slate)" />
                  <circle cx={layout.xs[index]} cy={layout.y(activeM.value)} r={4.5} fill="#fff" />
                </g>
              )}
            </g>
          </svg>
        )}
        {layout && (
          <ScrubOverlay
            left={margin.left} top={margin.top} width={innerW} height={innerH} label={label} max={baseline.length - 1} index={index} handlers={handlers}
            valuetext={activeB && activeM && !hideAmounts ? `${monthYear(activeB.date)}: ${money(activeB.value)} → ${money(activeM.value)}` : undefined}
          />
        )}
        {index !== null && activeB && activeM && layout && !hideAmounts && (
          <div
            className="chart-tooltip"
            style={{
              left: margin.left + layout.xs[index]! + (layout.xs[index]! > innerW * 0.6 ? -12 : 12),
              top: margin.top + 4,
              transform: layout.xs[index]! > innerW * 0.6 ? 'translateX(-100%)' : undefined,
            }}
            aria-hidden="true"
          >
            <span className="chart-tooltip__date">{monthYear(activeB.date)}</span>
            <span className="chart-tooltip__value" style={{ color: stroke }}>{money(activeM.value)}</span>
            <span className="chart-tooltip__sub">{t('whatIf.currentShort')} {money(activeB.value)}</span>
            <span className={`chart-tooltip__sub ${activeM.value >= activeB.value ? 'text-mint' : 'text-amber'}`}>
              {t('whatIf.difference')} {money(activeM.value - activeB.value, { sign: true })}
            </span>
          </div>
        )}
      </div>
    </figure>
  );
}
